import React from 'react';
import Icon from '../../../components/AppIcon';

const InterviewCertificate = ({
    userName = "Alex Chen",
    role = "Software Engineer",
    score = 82,
    topPercent = 8,
    id = "PX-9284-ALX",
    profilePicture = null,
    metrics = {
        architecture: 85,
        problemSolving: 78,
        communication: 92
    }
}) => {
    const [imgError, setImgError] = React.useState(false);

    return (
        <div id="capture-certificate" className="w-[800px] h-[450px] bg-white dark:bg-[#111216] border-4 border-emerald-500 dark:border-primary rounded-2xl flex overflow-hidden relative font-sans">
            {/* Grid Pattern Background */}
            <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
                style={{ backgroundImage: 'radial-gradient(#10E58F 1px, transparent 1px)', backgroundSize: '20px 20px' }} />

            {/* Left Section: Score & Rank */}
            <div className="w-[300px] h-full bg-emerald-50/30 dark:bg-[#0f1712]/40 flex flex-col items-center justify-center border-r border-emerald-100 dark:border-border relative">
                <div className="relative w-48 h-48 flex items-center justify-center">
                    {/* Progress Circle */}
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                        <circle
                            cx="50"
                            cy="50"
                            r="44"
                            stroke="rgba(16,185,129,0.1)"
                            strokeWidth="8"
                            fill="none"
                        />
                        <circle
                            cx="50"
                            cy="50"
                            r="44"
                            stroke="#10E58F"
                            strokeWidth="8"
                            fill="none"
                            strokeDasharray={2 * Math.PI * 44}
                            strokeDashoffset={2 * Math.PI * 44 * (1 - score / 100)}
                            strokeLinecap="round"
                        />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-5xl font-black text-slate-900 dark:text-slate-100 leading-none">{score}%</span>
                        <span className="text-[10px] font-black text-emerald-600 dark:text-primarytracking-[0.2em] mt-2">Performance Score</span>
                    </div>
                </div>

                <div className="mt-12 bg-slate-900 dark:bg-card text-emerald-400 dark:text-primary px-6 py-3 rounded-xl flex items-center gap-2 shadow-lg shadow-slate-200 dark:shadow-none">
                    <Icon name="Shield" size={16} className="fill-emerald-400/20" />
                    <span className="text-xs font-blacktracking-widest">Top {topPercent}% Worldwide</span>
                </div>
            </div>

            {/* Right Section: Content */}
            <div className="flex-1 p-10 flex flex-col relative">
                {/* Header */}
                <div className="flex justify-between items-start mb-10">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                            <Icon name="Play" size={20} color="#FFFFFF" className="ml-1" />
                        </div>
                        <span className="text-xl font-bold text-slate-800 dark:text-slate-200">prepxai</span>
                    </div>
                    <span className="text-[9px] font-black text-emerald-600/60 dark:text-primary/60tracking-widest">Certificate ID: {id}</span>
                </div>

                {/* Profile & Name */}
                <div className="flex items-center gap-8 mb-12">
                    {/* Hexagon Profile Picture */}
                    <div className="relative w-28 h-28">
                        <div className="absolute inset-0 bg-emerald-500 rotate-45 rounded-2xl" />
                        <div className="absolute inset-1 bg-white dark:bg-[#111216] rotate-45 rounded-2xl overflow-hidden">
                            <div className="-rotate-45 w-full h-full flex items-center justify-center bg-slate-100 dark:bg-card">
                                {profilePicture && !imgError ? (
                                    <img
                                        src={profilePicture}
                                        alt={userName}
                                        className="w-full h-full object-cover"
                                        crossOrigin="anonymous"
                                        onError={() => setImgError(true)}
                                    />
                                ) : (
                                    <Icon name="User" size={40} className="text-slate-300 dark:text-slate-600" />
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <h1 className="text-4xl font-black text-slate-900 dark:text-slate-100 leading-none">{userName}</h1>
                        <p className="text-base font-bold text-slate-500 dark:text-slate-400">{role}</p>
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-950/10 border border-emerald-100 dark:border-border rounded-lg mt-2">
                            <Icon name="CheckCircle" size={14} className="text-emerald-500" />
                            <span className="text-[10px] font-black text-emerald-600 dark:text-primarytracking-widest">Verified by prepxai</span>
                        </div>
                    </div>
                </div>

                {/* Footer Metrics */}
                <div className="mt-auto flex flex-col gap-2.5 max-w-[55%]">
                    {Object.entries(metrics).map(([key, val]) => (
                        <div key={key} className="flex items-center gap-4">
                            <span className="text-[8px] font-black text-slate-400 dark:text-slate-500tracking-widest min-w-[90px] shrink-0">
                                {key.replace(/([A-Z])/g, ' $1').trim()}
                            </span>
                            <div className="flex-1 h-1.5 bg-slate-50 dark:bg-muted rounded-full overflow-hidden border border-slate-100/50 dark:border-border relative">
                                <div
                                    className="h-full bg-emerald-500 rounded-full"
                                    style={{ width: `${val}%` }}
                                />
                            </div>
                            <span className="text-[9px] font-black text-slate-600 dark:text-slate-400 font-mono w-6 text-right shrink-0">
                                {val}%
                            </span>
                        </div>
                    ))}
                </div>

                {/* Verification QR Placeholder */}
                <div className="absolute bottom-10 right-10">
                    <div className="w-16 h-16 bg-slate-100 dark:bg-[#1c1e24] border border-slate-200 dark:border-border rounded-lg flex flex-col items-center justify-center p-2 shadow-sm">
                        <Icon name="QrCode" size={24} className="text-slate-400 dark:text-slate-600" />
                        <span className="text-[6px] font-bold text-slate-400 dark:text-slate-500mt-1">Scan to Verify</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InterviewCertificate;
